import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Landmark, 
  HeartPulse, 
  Factory, 
  ShoppingCart, 
  Building2, 
  Truck, 
  GraduationCap, 
  Zap,
  ArrowRight,
  CheckCircle2
} from 'lucide-react';

const Industries = () => {
  const industries = [
    {
      icon: <Landmark className="w-8 h-8" />,
      name: 'Financial Services',
      description: 'Regulatory compliance solutions, secure transaction systems, risk management platforms, and digital banking transformation.',
      solutions: ['Core Banking Modernization', 'Payment Gateway Integration', 'Risk Management Systems', 'Regulatory Compliance']
    },
    {
      icon: <HeartPulse className="w-8 h-8" />,
      name: 'Healthcare & Life Sciences',
      description: 'HIPAA-compliant systems, patient management platforms, healthcare data integration, and clinical trial management.',
      solutions: ['EHR Integration', 'Patient Portals', 'Medical Billing Systems', 'Telehealth Platforms']
    },
    {
      icon: <Factory className="w-8 h-8" />,
      name: 'Manufacturing',
      description: 'Supply chain optimization, IoT and Industry 4.0 solutions, quality management systems, and production planning tools.',
      solutions: ['ERP Implementation', 'Supply Chain Management', 'Quality Control Systems', 'Predictive Maintenance']
    },
    {
      icon: <ShoppingCart className="w-8 h-8" />,
      name: 'Retail & E-Commerce',
      description: 'Omnichannel platforms, inventory management systems, customer experience solutions, and payment gateway integration.',
      solutions: ['E-commerce Platforms', 'POS Integration', 'Inventory Management', 'Customer Analytics']
    },
    {
      icon: <Building2 className="w-8 h-8" />,
      name: 'Real Estate & Construction',
      description: 'Property management systems, project management tools, CRM for real estate, and document management solutions.',
      solutions: ['Property Management', 'Project Tracking', 'CRM Solutions', 'Document Management']
    },
    {
      icon: <Truck className="w-8 h-8" />,
      name: 'Logistics & Supply Chain',
      description: 'Fleet management systems, warehouse management, route optimization, and real-time tracking solutions.',
      solutions: ['Fleet Management', 'Warehouse Systems', 'Route Optimization', 'Tracking Solutions']
    },
    {
      icon: <GraduationCap className="w-8 h-8" />,
      name: 'Education',
      description: 'Learning management systems, student information systems, online assessment platforms, and campus management.',
      solutions: ['LMS Implementation', 'Student Portals', 'Assessment Platforms', 'Campus Management']
    },
    {
      icon: <Zap className="w-8 h-8" />,
      name: 'Energy & Utilities',
      description: 'Smart grid solutions, asset management, customer information systems, and field service management.',
      solutions: ['Smart Grid', 'Asset Management', 'Customer Systems', 'Field Service']
    }
  ];

  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative py-24 gradient-hero">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 right-10 w-96 h-96 bg-accent rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <span className="text-teal font-semibold text-sm uppercase tracking-wider">Industries</span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mt-4 mb-6">
              Industry-Specific Solutions
            </h1>
            <p className="text-lg text-white/80">
              Deep domain expertise combined with technical excellence to deliver solutions 
              that address your industry's unique challenges and requirements.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Industries Grid */}
      <section className="section bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4">
              Industries We Serve
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              We understand that every industry has unique challenges. Our consultants 
              bring deep domain knowledge to deliver solutions that drive results.
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid md:grid-cols-2 gap-8"
          >
            {industries.map((industry, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="card-hover p-8"
              >
                <div className="flex items-start gap-6">
                  <div className="w-16 h-16 bg-accent/10 rounded-xl flex items-center justify-center text-accent flex-shrink-0">
                    {industry.icon}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-primary mb-3">{industry.name}</h3>
                    <p className="text-slate-600 mb-4">{industry.description}</p>
                    
                    <div className="flex flex-wrap gap-2 mb-4">
                      {industry.solutions.map((solution, idx) => (
                        <span 
                          key={idx}
                          className="px-3 py-1 bg-slate-100 text-slate-600 text-xs rounded-full"
                        >
                          {solution}
                        </span>
                      ))}
                    </div>

                    <Link 
                      to="/contact"
                      className="inline-flex items-center text-accent font-semibold hover:text-accent-dark transition-colors"
                    >
                      Discuss Your Needs
                      <ArrowRight className="w-4 h-4 ml-1" />
                    </Link>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Approach Section */}
      <section className="section bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-accent font-semibold text-sm uppercase tracking-wider">Our Approach</span>
              <h2 className="text-3xl md:text-4xl font-bold text-primary mt-2 mb-6">
                Industry Expertise That Delivers Results
              </h2>
              <div className="space-y-4">
                <p className="text-slate-600">
                  At TeqDeepSeek Solutions, we don't believe in one-size-fits-all solutions. 
                  Each industry has its own regulatory requirements, operational challenges, 
                  and competitive dynamics.
                </p>
                <p className="text-slate-600">
                  Our approach combines deep industry knowledge with technical expertise to 
                  deliver solutions that are not just technically sound, but also aligned with 
                  your business objectives and industry best practices.
                </p>
              </div>

              <div className="mt-8 space-y-4">
                {[
                  'Industry-specific compliance expertise',
                  'Proven solutions for sector challenges',
                  'Dedicated industry-focused teams',
                  'Continuous learning and adaptation'
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 text-teal flex-shrink-0" />
                    <span className="text-slate-700">{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <img
                src="https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&h=600&fit=crop"
                alt="Industry expertise"
                className="rounded-2xl shadow-xl w-full"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section bg-accent">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Let's Discuss Your Industry Challenges
            </h2>
            <p className="text-white/80 text-lg mb-8">
              Our industry experts are ready to understand your unique needs and 
              design tailored solutions.
            </p>
            <Link to="/contact" className="btn bg-white text-accent hover:bg-slate-100 btn-lg">
              Schedule a Consultation
              <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Industries;
